import React, { useState, useRef, useEffect } from 'react';
import { colors, fonts, spacing, radii, layout, shadows } from '../styles/theme';
import type { TreeNode } from '../types';

interface HeaderProps {
  phase?: string;
  area?: string;
  cartCount: number;
  showCart?: boolean;
  cartIds?: string[];
  tree?: TreeNode[];
}

function findNodeById(nodes: TreeNode[], id: string): TreeNode | null {
  for (const n of nodes) {
    if (n.id === id) return n;
    const found = findNodeById(n.children, id);
    if (found) return found;
  }
  return null;
}

function getMainCartIds(cartIds: string[]): string[] {
  return cartIds.filter(
    (id) => !cartIds.some((other) => other !== id && (id === other || id.startsWith(other + '.')))
  );
}

const Header: React.FC<HeaderProps> = ({
  phase,
  area,
  cartCount,
  showCart = true,
  cartIds = [],
  tree = [],
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!dropdownOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [dropdownOpen]);

  const mainIds = getMainCartIds(cartIds);
  const mainQuestions = tree.length
    ? mainIds
        .map((id) => findNodeById(tree, id))
        .filter((n): n is TreeNode => n !== null)
        .map((n) => ({ id: n.id, question: n.question }))
    : [];

  const canOpenDropdown = showCart && (cartIds.length > 0 && tree.length > 0);

  return (
    <header style={styles.header}>
      <div style={styles.container}>
        <div style={styles.title}>
          The Shopping Bag Tool
        </div>

        {(phase || area) && (
          <div style={styles.context}>
            {phase && <span style={styles.phase}>{phase}</span>}
            {phase && area && <span style={styles.separator}>·</span>}
            {area && <span style={styles.area}>{area}</span>}
          </div>
        )}

        {showCart && (
          <div style={styles.cartWrapper} ref={dropdownRef}>
            <div
              role="button"
              tabIndex={0}
              onClick={() => canOpenDropdown && setDropdownOpen((o) => !o)}
              onKeyDown={(e) => {
                if (canOpenDropdown && (e.key === 'Enter' || e.key === ' ')) {
                  e.preventDefault();
                  setDropdownOpen((o) => !o);
                }
              }}
              style={{
                ...styles.cartTrigger,
                cursor: canOpenDropdown ? 'pointer' : 'default',
              }}
              aria-expanded={dropdownOpen}
              aria-haspopup="true"
              aria-label={dropdownOpen ? 'Close bag' : 'Open bag to see items'}
            >
              <div style={styles.cartBadge}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
                  <line x1="3" y1="6" x2="21" y2="6" />
                  <path d="M16 10a4 4 0 0 1-8 0" />
                </svg>
                {cartCount > 0 && (
                  <span style={styles.cartCountBadge}>{cartCount}</span>
                )}
              </div>
            </div>
            {dropdownOpen && mainQuestions.length > 0 && (
              <div style={styles.dropdown}>
                <div style={styles.dropdownTitle}>In your bag</div>
                <ul style={styles.dropdownList}>
                  {mainQuestions.map(({ id, question }) => (
                    <li key={id} style={styles.dropdownItem}>
                      <span style={styles.dropdownItemId}>{id}</span>
                      <span style={styles.dropdownItemQuestion}>{question}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </header>
  );
};

const styles: Record<string, React.CSSProperties> = {
  header: {
    position: 'sticky',
    top: 0,
    zIndex: 100,
    backgroundColor: colors.white,
    borderBottom: `1px solid ${colors.border}`,
    boxShadow: shadows.sm,
    height: layout.headerHeight,
    display: 'flex',
    alignItems: 'center',
  },
  container: {
    width: '100%',
    maxWidth: layout.maxWidth,
    margin: '0 auto',
    padding: `0 ${spacing[6]}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  title: {
    fontSize: fonts.lg,
    fontWeight: fonts.semibold,
    color: colors.text,
    flex: '0 0 auto',
  },
  context: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing[2],
    color: colors.textSecondary,
    fontSize: fonts.sm,
  },
  phase: {
    fontWeight: fonts.medium,
    color: colors.text,
  },
  separator: {
    color: colors.border,
  },
  area: {
    maxWidth: '200px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  cartWrapper: {
    flex: '0 0 auto',
    position: 'relative',
  },
  cartTrigger: {
    display: 'inline-flex',
    alignItems: 'center',
    outline: 'none',
    padding: spacing[2],
  },
  cartBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing[1],
    color: colors.textSecondary,
  },
  cartCountBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '20px',
    height: '20px',
    padding: '0 6px',
    fontSize: fonts.xs,
    fontWeight: fonts.semibold,
    color: colors.white,
    backgroundColor: colors.primary,
    borderRadius: radii.full,
    lineHeight: 1,
  },
  dropdown: {
    position: 'absolute',
    top: '100%',
    right: 0,
    marginTop: spacing[2],
    minWidth: 320,
    maxWidth: 420,
    maxHeight: 360,
    overflowY: 'auto',
    backgroundColor: colors.white,
    border: `1px solid ${colors.border}`,
    borderRadius: radii.lg,
    boxShadow: shadows.lg,
    zIndex: 200,
    padding: spacing[4],
  },
  dropdownTitle: {
    fontSize: fonts.sm,
    fontWeight: fonts.semibold,
    color: colors.text,
    marginBottom: spacing[3],
    paddingBottom: spacing[2],
    borderBottom: `1px solid ${colors.border}`,
  },
  dropdownList: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
  },
  dropdownItem: {
    display: 'flex',
    gap: spacing[2],
    padding: `${spacing[2]} 0`,
    borderBottom: `1px solid ${colors.borderLight}`,
    fontSize: fonts.sm,
  },
  dropdownItemId: {
    flex: '0 0 auto',
    fontWeight: fonts.semibold,
    color: colors.primary,
  },
  dropdownItemQuestion: {
    flex: 1,
    color: colors.textSecondary,
    lineHeight: fonts.lineHeightRelaxed,
  },
};

export default Header;
